import {
  ArrowRightOutlined,
  BarChartOutlined,
  BellOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FundOutlined,
  HeartOutlined,
  MessageOutlined,
  ProjectOutlined,
  ReloadOutlined,
  RiseOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Alert, Button, Card, Col, Descriptions, Empty, Row, Skeleton, Space, Spin, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { useProjectAlerts } from '../alerts/useProjectAlerts';
import { getCurrentUser } from '../api/auth';
import type { CurrentUserResponse } from '../api/auth';
import { ApiError } from '../api/errors';
import { SalesOpportunityStage, salesOpportunityStageNames } from '../api/opportunities';
import type { SalesOpportunityStageValue } from '../api/opportunities';
import { ProjectHealth, projectHealthNames, projectStatusNames } from '../api/projects';
import type { MyProjectTaskItem, ProjectHealthValue } from '../api/projects';
import { useAuth } from '../auth/useAuth';
import { StatTiles } from '../layout/StatTiles';
import type { StatTile } from '../layout/StatTiles';
import { AppRoutes } from '../routes';
import { BrandColors } from '../theme/brand';
import { BarList } from './home/BarList';
import type { BarDatum } from './home/BarList';
import { formatCompactMoney, useDashboardData } from './home/useDashboardData';
import type { Loadable } from './home/useDashboardData';
import { ProjectAlertList } from './projects/ProjectAlertList';
import { formatDate } from './projects/projectDisplay';

/** Thứ và ngày hôm nay theo cách viết tiếng Việt, ví dụ "Thứ Ba, 03/11/2026". */
function todayLabel(): string {
  return new Intl.DateTimeFormat('vi-VN', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date());
}

/**
 * Màu, biểu tượng của từng mức sức khỏe dự án.
 *
 * Bốn màu trạng thái này KHÔNG phân biệt được với nhau chỉ bằng màu (cam và gold quá gần — đã chạy
 * bộ kiểm bảng màu), nên mỗi mức luôn có biểu tượng riêng và tên mức đứng cạnh; màu chỉ là tín hiệu
 * phụ. Cũng vì vậy chúng được vẽ thành từng dòng có nhãn, không ghép thành một thanh nhiều màu.
 */
const healthStyle: Readonly<Record<ProjectHealthValue, { color: string; icon: ReactNode }>> = {
  [ProjectHealth.Good]: { color: BrandColors.green, icon: <CheckCircleOutlined /> },
  [ProjectHealth.NeedsAttention]: { color: BrandColors.gold, icon: <ExclamationCircleOutlined /> },
  [ProjectHealth.AtRisk]: { color: '#D97B29', icon: <WarningOutlined /> },
  [ProjectHealth.Critical]: { color: BrandColors.red, icon: <CloseCircleOutlined /> },
};

/** Bốn giai đoạn còn mở của phễu, theo thứ tự thương vụ đi qua. */
const openStages: readonly SalesOpportunityStageValue[] = [
  SalesOpportunityStage.Survey,
  SalesOpportunityStage.Quoting,
  SalesOpportunityStage.Negotiating,
  SalesOpportunityStage.AwaitingPurchaseOrder,
];

function percent(part: number, whole: number): string {
  return whole === 0 ? '0%' : `${Math.round((part / whole) * 100).toString()}%`;
}

/** Tiêu đề thẻ: biểu tượng trong ô xanh nhạt và tên thẻ — mọi thẻ của trang chủ cùng một kiểu. */
function CardTitle({ icon, children }: { readonly icon: ReactNode; readonly children: ReactNode }): ReactNode {
  return (
    <Space size={10}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: 8,
          color: BrandColors.blue,
          background: BrandColors.blueSoft,
        }}
      >
        {icon}
      </span>
      <span>{children}</span>
    </Space>
  );
}

/** Liên kết "xem thêm" ở góc thẻ. */
function MoreLink({ to, children }: { readonly to: string; readonly children: ReactNode }): ReactNode {
  const navigate = useNavigate();

  return (
    <Button
      type="link"
      size="small"
      style={{ paddingInline: 0 }}
      onClick={() => {
        void navigate(to);
      }}
    >
      {children} <ArrowRightOutlined />
    </Button>
  );
}

/** Nội dung thẻ theo trạng thái nạp: khung chờ, lỗi, hoặc dữ liệu. */
function Loaded<T>({
  source,
  children,
}: {
  readonly source: Loadable<T>;
  readonly children: (data: T) => ReactNode;
}): ReactNode {
  if (source.state === 'loading') {
    return <Skeleton active paragraph={{ rows: 4 }} title={false} />;
  }

  if (source.state === 'error') {
    return <Alert type="error" showIcon message={source.message} />;
  }

  return children(source.data);
}

/** Năm việc nên làm trước: quá hạn trước, rồi theo hạn gần nhất; việc không có hạn xếp cuối. */
function upcomingTasks(tasks: readonly MyProjectTaskItem[]): MyProjectTaskItem[] {
  return [...tasks]
    .sort((left, right) => {
      if (left.isOverdue !== right.isOverdue) {
        return left.isOverdue ? -1 : 1;
      }

      return (left.plannedEndDate ?? '9999-12-31').localeCompare(right.plannedEndDate ?? '9999-12-31');
    })
    .slice(0, 5);
}

/**
 * Trang chủ — bảng điều khiển sau khi đăng nhập.
 *
 * Mỗi thẻ trả lời MỘT câu hỏi và có một lối vào để xử lý, đúng mẫu "bảng điều khiển" của tài liệu
 * thiết kế giao diện: không có biểu đồ trang trí. Thứ tự từ trên xuống là thứ tự người ta cần biết
 * vào buổi sáng: con số tổng quát, tình hình dự án, việc cần chú ý và việc của mình, rồi tới bán
 * hàng.
 *
 * Thẻ nào người dùng không có quyền thì không hiện và cũng không gọi máy chủ.
 *
 * Thông tin phiên đăng nhập vẫn giữ ở cuối, thu gọn: nó gọi một điểm cuối cần phiếu xác thực nên
 * chứng minh cả chuỗi đăng nhập đang hoạt động.
 */
export function HomePage(): ReactNode {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { summary, isLoading, reload: reloadAlerts } = useProjectAlerts();
  const dashboard = useDashboardData();

  const [serverView, setServerView] = useState<CurrentUserResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const view = await getCurrentUser();

        if (!controller.signal.aborted) {
          setServerView(view);
        }
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không gọi được tới máy chủ.',
        );
      }
    })();

    return () => {
      controller.abort();
    };
  }, []);

  const { projects, myTasks, pipeline, contacts } = dashboard;

  const projectData = projects?.state === 'ready' ? projects.data : undefined;
  const taskData = myTasks?.state === 'ready' ? myTasks.data : undefined;
  const pipelineData = pipeline?.state === 'ready' ? pipeline.data : undefined;
  const contactData = contacts?.state === 'ready' ? contacts.data : undefined;

  const tiles: StatTile[] = [
    ...(projects === null
      ? []
      : [
          {
            key: 'projects',
            label: 'Dự án đang mở',
            icon: <ProjectOutlined />,
            color: BrandColors.blue,
            value: projectData?.openCount,
            onClick: () => {
              void navigate(AppRoutes.Project);
            },
          },
          {
            key: 'overdue',
            label: 'Dự án quá hạn',
            icon: projectData?.overdueCount === 0 ? <CheckCircleOutlined /> : <WarningOutlined />,
            color: projectData?.overdueCount === 0 ? BrandColors.green : BrandColors.red,
            value: projectData?.overdueCount,
            hint: 'Đã quá mốc cam kết',
          },
        ]),
    ...(myTasks === null
      ? []
      : [
          {
            key: 'tasks',
            label: 'Việc của tôi',
            icon: <CheckSquareOutlined />,
            color: BrandColors.ink,
            value: taskData?.length,
            hint:
              taskData === undefined
                ? undefined
                : `${taskData.filter((task) => task.isOverdue).length.toString()} việc quá hạn`,
            onClick: () => {
              void navigate(AppRoutes.MyTasks);
            },
          },
        ]),
    ...(contacts === null
      ? []
      : [
          {
            key: 'contacts',
            label: 'Liên hệ cần xử lý',
            icon: <MessageOutlined />,
            color: BrandColors.gold,
            value: contactData?.needsAction,
            hint: contactData === undefined ? undefined : `${contactData.new.toString()} chưa ai gọi`,
            onClick: () => {
              void navigate(AppRoutes.ContactRequest);
            },
          },
        ]),
    ...(pipeline === null
      ? []
      : [
          {
            key: 'pipeline',
            label: 'Cơ hội đang theo đuổi',
            icon: <RiseOutlined />,
            color: BrandColors.blue,
            value: pipelineData?.openCount,
            hint:
              pipelineData === undefined
                ? undefined
                : `Giá trị dự kiến ${formatCompactMoney(pipelineData.openValue)}`,
            onClick: () => {
              void navigate(AppRoutes.Opportunity);
            },
          },
        ]),
  ];

  const showAlerts = summary !== null || isLoading;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section className="erp-welcome">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div className="erp-overline">{todayLabel()}</div>
            <Typography.Title level={3} style={{ margin: '6px 0 0', fontWeight: 700 }}>
              Xin chào {user?.fullName}
            </Typography.Title>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              dashboard.reload();
              reloadAlerts();
            }}
          >
            Làm mới số liệu
          </Button>
        </div>
      </section>

      {user?.mustChangePassword === true && (
        <Alert
          type="warning"
          showIcon
          message="Tài khoản này đang dùng mật khẩu tạm"
          description={
            'Mật khẩu tạm được đặt lúc cài đặt hệ thống nên có thể đã đi qua nhiều tay. '
            + 'Màn hình đổi mật khẩu sẽ có ở đợt tiếp theo.'
          }
        />
      )}

      {tiles.length === 0 ? null : <StatTiles items={tiles} />}

      <Row gutter={[16, 16]}>
        {projects === null ? null : (
          <>
            <Col xs={24} xl={14}>
              <Card
                className="erp-dash-card"
                title={<CardTitle icon={<BarChartOutlined />}>Dự án đang mở theo trạng thái</CardTitle>}
                extra={<MoreLink to={AppRoutes.ProjectBoard}>Bảng tiến độ</MoreLink>}
              >
                <Loaded source={projects}>
                  {(stats) =>
                    stats.openCount === 0 ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có dự án nào đang mở." />
                    ) : (
                      <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <BarList
                          items={[...stats.openByStatus.entries()].map(([status, count]): BarDatum => ({
                            key: String(status),
                            label: projectStatusNames[status],
                            value: count,
                            tooltip: `${projectStatusNames[status]}: ${count.toString()} dự án (${percent(count, stats.openCount)})`,
                          }))}
                          onSelect={() => {
                            void navigate(AppRoutes.Project);
                          }}
                        />
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {stats.openCount.toLocaleString('vi-VN')} dự án đang mở ·{' '}
                          {stats.closedCount.toLocaleString('vi-VN')} đã đóng hoặc đã hủy
                          {stats.isTruncated ? ' · tính trên 200 dự án mới nhất' : ''}
                        </Typography.Text>
                      </Space>
                    )
                  }
                </Loaded>
              </Card>
            </Col>

            <Col xs={24} xl={10}>
              <Card
                className="erp-dash-card"
                title={<CardTitle icon={<HeartOutlined />}>Sức khỏe dự án đang mở</CardTitle>}
                extra={<MoreLink to={AppRoutes.Project}>Danh sách</MoreLink>}
              >
                <Loaded source={projects}>
                  {(stats) =>
                    stats.openCount === 0 ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có dự án nào đang mở." />
                    ) : (
                      <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <BarList
                          items={[...stats.openByHealth.entries()].map(([level, count]): BarDatum => ({
                            key: String(level),
                            label: projectHealthNames[level],
                            value: count,
                            color: healthStyle[level].color,
                            icon: healthStyle[level].icon,
                            tooltip: `${projectHealthNames[level]}: ${count.toString()} dự án (${percent(count, stats.openCount)})`,
                          }))}
                        />
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {(stats.openByHealth.get(ProjectHealth.AtRisk) ?? 0)
                            + (stats.openByHealth.get(ProjectHealth.Critical) ?? 0) === 0
                            ? 'Không dự án nào ở mức có vấn đề hay nghiêm trọng.'
                            : `${percent(
                                (stats.openByHealth.get(ProjectHealth.AtRisk) ?? 0)
                                  + (stats.openByHealth.get(ProjectHealth.Critical) ?? 0),
                                stats.openCount,
                              )} dự án đang mở ở mức có vấn đề hoặc nghiêm trọng.`}
                        </Typography.Text>
                      </Space>
                    )
                  }
                </Loaded>
              </Card>
            </Col>
          </>
        )}

        {/*
          Thẻ cảnh báo chỉ hiện với người có quyền xem công việc. Với người không có quyền thì bộ cung
          cấp trạng thái không gọi máy chủ, nên ở đây không có gì để hiện — và một thẻ rỗng mang tên
          "những việc cần chú ý" còn khó hiểu hơn là không có thẻ nào.
        */}
        {showAlerts ? (
          <Col xs={24} xl={myTasks === null ? 24 : 14}>
            <Card
              className="erp-dash-card"
              title={<CardTitle icon={<BellOutlined />}>Những việc cần chú ý</CardTitle>}
            >
              <ProjectAlertList summary={summary} isLoading={isLoading} onReload={reloadAlerts} />
            </Card>
          </Col>
        ) : null}

        {myTasks === null ? null : (
          <Col xs={24} xl={showAlerts ? 10 : 24}>
            <Card
              className="erp-dash-card"
              title={<CardTitle icon={<CheckSquareOutlined />}>Việc của tôi sắp đến hạn</CardTitle>}
              extra={<MoreLink to={AppRoutes.MyTasks}>Tất cả</MoreLink>}
            >
              <Loaded source={myTasks}>
                {(tasks) =>
                  tasks.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Bạn không có việc nào đang mở." />
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {upcomingTasks(tasks).map((task) => (
                        <div
                          key={task.assignmentId}
                          className="erp-bar-row erp-bar-row--clickable"
                          style={{ gridTemplateColumns: 'minmax(0, 1fr) auto' }}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            void navigate(`${AppRoutes.Project}/${task.projectId}/cong-viec`);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              void navigate(`${AppRoutes.Project}/${task.projectId}/cong-viec`);
                            }
                          }}
                        >
                          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                            <Typography.Text strong ellipsis>
                              {task.taskName}
                            </Typography.Text>
                            <Typography.Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                              {task.projectCode} · {task.wbsCode} · {task.progressPercent.toString()}%
                            </Typography.Text>
                          </div>
                          {task.isOverdue ? (
                            <Tag color="red" style={{ marginInlineEnd: 0 }}>
                              Quá hạn {formatDate(task.plannedEndDate)}
                            </Tag>
                          ) : (
                            <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                              {task.plannedEndDate === null ? 'Chưa có hạn' : `Hạn ${formatDate(task.plannedEndDate)}`}
                            </Typography.Text>
                          )}
                        </div>
                      ))}
                    </div>
                  )
                }
              </Loaded>
            </Card>
          </Col>
        )}

        {pipeline === null ? null : (
          <Col xs={24} xl={contacts === null ? 24 : 14}>
            <Card
              className="erp-dash-card"
              title={<CardTitle icon={<FundOutlined />}>Phễu cơ hội bán hàng</CardTitle>}
              extra={<MoreLink to={AppRoutes.Opportunity}>Mở phễu</MoreLink>}
            >
              <Loaded source={pipeline}>
                {(data) => {
                  const byStage = new Map(data.stages.map((stage) => [stage.stage, stage]));

                  return (
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                      <BarList
                        items={openStages.map((stage): BarDatum => {
                          const summaryOfStage = byStage.get(stage);
                          const count = summaryOfStage?.count ?? 0;
                          const value = summaryOfStage?.totalValue ?? 0;

                          return {
                            key: String(stage),
                            label: salesOpportunityStageNames[stage],
                            value: count,
                            ...(value > 0 ? { extra: formatCompactMoney(value) } : {}),
                            tooltip: `${salesOpportunityStageNames[stage]}: ${count.toString()} cơ hội, giá trị dự kiến ${formatCompactMoney(value)}`,
                          };
                        })}
                        onSelect={() => {
                          void navigate(AppRoutes.Opportunity);
                        }}
                      />
                      <Space size={[16, 4]} wrap>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          Thắng <Typography.Text strong>{data.wonCount}</Typography.Text>
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          Thua <Typography.Text strong>{data.lostCount}</Typography.Text>
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          Khách dừng <Typography.Text strong>{data.abandonedCount}</Typography.Text>
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          Tỉ lệ thắng{' '}
                          <Typography.Text strong>
                            {data.winRatePercent === null ? '—' : `${Math.round(data.winRatePercent).toString()}%`}
                          </Typography.Text>
                        </Typography.Text>
                      </Space>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Giá trị cộng theo số, chưa quy đổi đồng tiền.
                      </Typography.Text>
                    </Space>
                  );
                }}
              </Loaded>
            </Card>
          </Col>
        )}

        {contacts === null ? null : (
          <Col xs={24} xl={pipeline === null ? 24 : 10}>
            <Card
              className="erp-dash-card"
              title={<CardTitle icon={<MessageOutlined />}>Liên hệ từ website</CardTitle>}
              extra={<MoreLink to={AppRoutes.ContactRequest}>Mở danh sách</MoreLink>}
            >
              <Loaded source={contacts}>
                {(counts) => (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <BarList
                      items={[
                        { key: 'new', label: 'Chưa liên hệ', value: counts.new },
                        { key: 'in-progress', label: 'Đang liên hệ', value: counts.inProgress },
                        { key: 'unreachable', label: 'Không liên lạc được', value: counts.unreachable },
                        { key: 'contacted', label: 'Đã liên hệ', value: counts.contacted },
                        { key: 'irrelevant', label: 'Không phù hợp', value: counts.irrelevant },
                      ]}
                      onSelect={() => {
                        void navigate(AppRoutes.ContactRequest);
                      }}
                    />
                    <Button
                      type={counts.needsAction > 0 ? 'primary' : 'default'}
                      block
                      onClick={() => {
                        void navigate(AppRoutes.ContactRequest);
                      }}
                    >
                      {counts.needsAction > 0
                        ? `Gọi lại khách — còn ${counts.needsAction.toString()} liên hệ cần xử lý`
                        : 'Không còn liên hệ nào cần xử lý'}
                    </Button>
                  </Space>
                )}
              </Loaded>
            </Card>
          </Col>
        )}
      </Row>

      <Card size="small" title="Phiên làm việc, theo máy chủ">
        {errorMessage !== null && <Alert type="error" showIcon message={errorMessage} />}

        {errorMessage === null && serverView === null && <Spin size="small" />}

        {serverView !== null && (
          <Descriptions size="small" column={{ xs: 1, md: 2, xl: 4 }}>
            <Descriptions.Item label="Tên đăng nhập">{serverView.userName}</Descriptions.Item>
            <Descriptions.Item label="Ngày làm việc">{serverView.businessDate}</Descriptions.Item>
            <Descriptions.Item label="Định danh người dùng">
              <Typography.Text code style={{ fontSize: 12 }}>{serverView.userId}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Pháp nhân">
              <Typography.Text code style={{ fontSize: 12 }}>{serverView.companyId}</Typography.Text>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>
    </div>
  );
}
